import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { loadGoogleMaps, gmapsAuthFailed } from "@/lib/googleMaps";

// Legacy address_components (long_name/short_name)
const comp = (components, type, short = false) => {
  const c = (components || []).find((x) => x.types.includes(type));
  return c ? (short ? c.short_name : c.long_name) : "";
};
// New Places API addressComponents (longText/shortText)
const compNew = (components, type, short = false) => {
  const c = (components || []).find((x) => (x.types || []).includes(type));
  return c ? (short ? c.shortText : c.longText) : "";
};

export const AddressAutocomplete = ({ value, onChange, onSelect, ...props }) => {
  const boxRef = useRef(null);
  const acSvc = useRef(null);
  const placesSvc = useRef(null);
  const token = useRef(null);
  const debounce = useRef(null);
  const [preds, setPreds] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    if (gmapsAuthFailed()) return;
    loadGoogleMaps()
      .then(async (g) => {
        if (!g?.maps) return;
        try {
          // With loading=async, places may not be available synchronously; ensure it's imported.
          const places = (g.maps.places && g.maps.places.AutocompleteService)
            ? g.maps.places
            : await g.maps.importLibrary("places");
          acSvc.current = new places.AutocompleteService();
          placesSvc.current = new places.PlacesService(document.createElement("div"));
          token.current = new places.AutocompleteSessionToken();
        } catch (e) { /* fall back to plain input */ }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const runQuery = (text) => {
    if (!acSvc.current || !text || text.trim().length < 3) { setPreds([]); setOpen(false); return; }
    acSvc.current.getPlacePredictions(
      { input: text, componentRestrictions: { country: "us" }, types: ["address"], sessionToken: token.current },
      (list, status) => {
        if (status === "OK" && list && list.length) { setPreds(list); setOpen(true); setActive(-1); }
        else { setPreds([]); setOpen(false); }
      },
    );
  };

  const handleChange = (e) => {
    onChange?.(e);
    const text = e.target.value;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runQuery(text), 250);
  };

  const emit = (obj) => onSelect?.(obj);
  const refreshToken = () => { try { token.current = new window.google.maps.places.AutocompleteSessionToken(); } catch (e) { /* noop */ } };

  const choose = (pred) => {
    setOpen(false);
    setPreds([]);
    const fallbackStreet = pred.structured_formatting?.main_text || pred.description || "";
    if (!placesSvc.current) { emit({ address: fallbackStreet, city: "", state: "", zip: "", lat: null, lng: null }); return; }
    placesSvc.current.getDetails(
      { placeId: pred.place_id, fields: ["address_components", "geometry", "formatted_address"], sessionToken: token.current },
      async (p, status) => {
        if (status === "OK" && p) {
          const c = p.address_components || [];
          emit({
            address: [comp(c, "street_number"), comp(c, "route")].filter(Boolean).join(" ") || fallbackStreet,
            city: comp(c, "locality") || comp(c, "sublocality") || comp(c, "postal_town"),
            state: comp(c, "administrative_area_level_1", true),
            zip: comp(c, "postal_code"),
            lat: p.geometry?.location?.lat?.() ?? null,
            lng: p.geometry?.location?.lng?.() ?? null,
          });
          refreshToken();
          return;
        }
        // Fallback: new Places API Place.fetchFields (for keys where legacy getDetails is blocked)
        try {
          const { Place } = await window.google.maps.importLibrary("places");
          const place = new Place({ id: pred.place_id });
          await place.fetchFields({ fields: ["addressComponents", "location", "formattedAddress"] });
          const c = place.addressComponents || [];
          emit({
            address: [compNew(c, "street_number"), compNew(c, "route")].filter(Boolean).join(" ") || fallbackStreet,
            city: compNew(c, "locality") || compNew(c, "sublocality") || compNew(c, "postal_town"),
            state: compNew(c, "administrative_area_level_1", true),
            zip: compNew(c, "postal_code"),
            lat: place.location?.lat?.() ?? null,
            lng: place.location?.lng?.() ?? null,
          });
        } catch (e) {
          emit({ address: fallbackStreet, city: "", state: "", zip: "", lat: null, lng: null });
        }
        refreshToken();
      },
    );
  };

  const onKeyDown = (e) => {
    if (!open || !preds.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, preds.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(preds[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <Input value={value} onChange={handleChange} onKeyDown={onKeyDown} autoComplete="off"
             data-testid="address-autocomplete-input" {...props} />
      {open && preds.length > 0 && (
        <ul className="absolute z-[80] mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg"
            data-testid="address-suggestions">
          {preds.map((p, i) => (
            <li key={p.place_id}
                onMouseDown={(e) => { e.preventDefault(); choose(p); }}
                onMouseEnter={() => setActive(i)}
                data-testid={`address-suggestion-${i}`}
                className={`cursor-pointer px-3 py-2 text-sm ${i === active ? "bg-accent text-accent-foreground" : "hover:bg-accent"}`}>
              <span className="font-medium">{p.structured_formatting?.main_text || p.description}</span>
              {p.structured_formatting?.secondary_text && (
                <span className="ml-1 text-xs text-muted-foreground">{p.structured_formatting.secondary_text}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
